import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        accent: "bg-accent text-accent-foreground hover:bg-accent/70",
        success:
          "border-transparent bg-success text-success-foreground hover:bg-success/80",
        warning:
          "border-transparent bg-warning text-warning-foreground hover:bg-warning/80",
      },
      type: {
        default: "px-2.5 py-0.5  rounded-full ",
        small: "px-1 py-1 text-[10px] h-4  rounded-full ",
        tight: "rounded px-1 py-0.5",
        indicator: "absolute top-0 right-0 w-2 h-2  rounded-full ",
      },
    },
    defaultVariants: {
      variant: "default",
      type: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * Renders a Badge component with customizable styles and additional properties.
 * @param {object} props - The properties passed to the Badge component.
 * @param {string} props.className - Optional additional class names for styling.
 * @param {string} props.variant - The variant style of the badge.
 * @param {string} props.type - The type style of the badge.
 * @param {...object} props - Additional props to be spread onto the component.
 * @returns {JSX.Element} A rendered Badge component.
 */
function Badge({ className, variant, type, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, type }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
